import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";

import EmptyPlaceholder from "@/components/ui/EmptyPlaceholder";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import ChartSkeleton from "./ChartSkeleton";
import { getChartOptions } from "./constants";
import CustomLegend from "./CustomLegend";
import { useChartData, useChartLegend } from "./hooks";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const Graph: React.FC<{
  usePromptId?: boolean;
  useParentReportId?: boolean;
  displayAll?: boolean;
  displayOnlyDatasetName?: boolean;
}> = ({
  usePromptId,
  useParentReportId,
  displayAll,
  displayOnlyDatasetName,
}) => {
  const [chartData, isLoading] = useChartData({
    usePromptId,
    useParentReportId,
    displayAll,
  });
  const chartRef = useRef<ChartJS<"line">>(null);

  const { legendItems, handleLegendItemClick, updateLegendItems } =
    useChartLegend(chartRef, chartData);

  // Add effect to handle chart initialization
  useEffect(() => {
    // This helps ensure the chart is initialized before trying to access it
    if (chartRef.current && chartData.datasets.length > 0) {
      // Small delay to ensure chart has fully rendered
      const timeoutId = setTimeout(() => {
        updateLegendItems();
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [chartData.datasets, updateLegendItems]);

  const chartOptions = getChartOptions();

  const modifiedOptions = useMemo(
    () => ({
      ...chartOptions,
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        ...chartOptions.plugins,
        legend: {
          ...chartOptions.plugins!.legend,
          display: false,
        },
      },
    }),
    [chartOptions],
  );
  return (
    <div className="flex w-full flex-col">
      {isLoading ? (
        <div className="flex-1 overflow-hidden pb-4 pt-2">
          <ChartSkeleton />
        </div>
      ) : chartData.datasets.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-4">
          <EmptyPlaceholder
            className="h-full"
            icon
            message="No evaluation data available. Apply different filters or run evaluations on this prompt."
          />
        </div>
      ) : (
        <div className="h-fullw-full flex flex-col overflow-hidden">
          <div className="flex-shrink-0 pb-2">
            <CustomLegend
              legendItems={legendItems}
              onLegendItemClick={handleLegendItemClick}
              displayOnlyDatasetName={displayOnlyDatasetName}
            />
          </div>
          <div className="relative min-h-0 w-full flex-1">
            <Line
              ref={chartRef}
              data={chartData}
              options={modifiedOptions}
              className="h-full w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(Graph);
